import React from 'react';
import {
  AccountApiResponse,
  CommonPageStep,
  Plans,
  SubscriptionPlanLabel,
  SubscriptionPlanPrice,
  subscriptionPlanName,
} from '..';
import { Button } from '@mui/material';
import { string } from 'prop-types';
import * as qs from 'query-string';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../common/ApiHelper';
import { queryString } from '../../common/CommonTools';
import { appConfig } from '../../common/ConfigLoader';
import { useAppSelector } from '../../store/hooks';
import { setAppParentPortalData } from '../../store/reducer/appParentPortalDataSlice';
import {
  AppStateAuthentication,
  AppUserProfile,
  ChildProfile,
  selectAppAuthentication,
  selectAppUserProfile,
  setAppPageStep,
  updateAppUserSubscription,
} from '../../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../../store/store';
import { SubscriptionLevelName } from '../signup/yourChildAge/YourChildAgeElement';

export interface ThankYouElementInterface {
  startBigBangLabButton: JSX.Element;
  contactUsButtonMobile: JSX.Element;
  subscriptionPlanLabel: string;
  subscriptionPlanPrice: string;
  subscriptionDate: string;
  subscriptionLevel: (userProfileData?: AppUserProfile) => string;
  subscriptionMethod: string;
  getPaymentInfoViaPaymentId: (
    authenticationData: AppStateAuthentication,
  ) => Promise<void>;
  getSubscriptionPlanSubLabel: (paymentTo: string) => string;
}

export const ThankYouElement = (): ThankYouElementInterface => {
  // Redux
  const authenticationData = useAppSelector(selectAppAuthentication);

  // Hooks
  const [paymentTarget, setPaymentTarget] = React.useState('');

  // Page Functions
  const getSubscriptionPlanLabel = (): string => {
    return subscriptionPlanName(paymentTarget);
  };

  const getSubscriptionPlanSubLabel = (paymentTo: string): string => {
    if (paymentTo.startsWith('SUBSCRIPTION')) {
      return 'All-in-one STEAM Curriculum Package';
    }

    switch (paymentTo) {
      case 'PURCHASE_PROJECT_ALPHA':
        return '6-Month Program';
      case 'PURCHASE_PROJECT_ALPHA_ANNUALLY':
        return '1-Year Program';
      case 'PURCHASE_PROJECT_ALPHA_BIANNUALLY':
        return '2-Year Program';
      default:
        return '';
    }
  };

  const getSubscriptionPlanPrice = (): string => {
    switch (paymentTarget) {
      case Plans.bblProjectAlpha:
        return SubscriptionPlanPrice.bblProjectAlpha;
      case Plans.bblProjectAlphaYearly:
        return SubscriptionPlanPrice.bblProjectAlphaYearly;
      case Plans.bblMonthlySubscription:
        return SubscriptionPlanPrice.bblMonthlySubscription;
      case Plans.bblHalfYearSubscription:
        return SubscriptionPlanPrice.bblHalfYearSubscription;
      case Plans.bblYearlySubscription:
      default:
        return SubscriptionPlanPrice.bblYearlySubscription;
    }
  };

  const getSubscriptionPlanLevel = (
    userProfileData?: AppUserProfile,
  ): string => {
    const childProfile: ChildProfile | undefined =
      userProfileData?.childProfile.first;
    const childLevel = childProfile?.child_profile_data.childLevel;
    return childLevel === 'Introductory'
      ? 'Introductory (4-5)'
      : childLevel === 'Intermediate'
      ? 'Intermediate (6-8)'
      : '';
  };

  const getPaymentInfoViaPaymentId = async (
    authenticationData: AppStateAuthentication,
  ): Promise<void> => {
    // CALLING ACCOUNT SERVER API TO FETCH PAYMENT INFO
    const parmsValue = qs.parse(window.location.search);
    console.log('parmsValue payment_id', parmsValue.payment_id);
    const paymentInfoEndpoint = `user/UserPaymentInformation`;
    const paymentInfoData = {
      paymentId: parmsValue.payment_id,
    };
    const paymentInfo = await apiHelper(
      SupportedServers.ACCOUNT,
      paymentInfoEndpoint,
      SupportedMethod.POST,
      paymentInfoData,
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        outgoingFormat: SupportedOutgoingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData.token,
          };
        },
      },
    );

    if (paymentInfo.success) {
      outsideComponentDispatch(
        setAppParentPortalData({
          dataKey: 'paymentRecord',
          data: paymentInfo.data.paymentRecord,
        }),
      );

      outsideComponentDispatch(
        setAppParentPortalData({
          dataKey: 'subscriptionRecord',
          data: paymentInfo.data.subscriptionRecord,
        }),
      );
    }
  };

  React.useEffect(() => {
    const parmsValue = queryString();
    setPaymentTarget(parmsValue.paymentTarget);
  }, []);

  return {
    startBigBangLabButton: (
      <Button
        variant="contained"
        endIcon={<img src="assets/thankYou/thank-you-play-icon.svg" />}
        onClick={() => {
          outsideComponentDispatch(
            setAppPageStep(CommonPageStep.CHILD_PROFILE),
          );
        }}
      >
        Start Big Bang Lab
      </Button>
    ),
    contactUsButtonMobile: (
      <Button
        variant="contained"
        endIcon={
          <img
            id="whatsapp-icon"
            alt=""
            src="assets/common/whatsapp-icon.svg"
          />
        }
        onClick={() => {
          window.open(
            'https://api.whatsapp.com/send/?phone=85230015422&text&app_absent=0',
            '_blank',
          );
        }}
      >
        Contact Us
      </Button>
    ),
    subscriptionPlanLabel: getSubscriptionPlanLabel(),
    subscriptionPlanPrice: getSubscriptionPlanPrice(),
    subscriptionDate: new Date().toDateString(),
    subscriptionLevel: getSubscriptionPlanLevel,
    subscriptionMethod: 'Card',
    getPaymentInfoViaPaymentId,
    getSubscriptionPlanSubLabel,
  };
};
